/**
 * UserProfileMenu — the standard TitleFlow profile chip + dropdown
 * (avatar initials, display name, role, logout). Mirrors the main
 * dashboard header chip (.user-badge / .initials global styles) so
 * secondary areas like Reports feel native to the app.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, LogOut } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../ui/dropdown-menu';

const ROLE_LABELS = {
  admin: 'Administrator',
  it_manager: 'IT Manager',
  it_support: 'IT Support',
  hr_manager: 'HR Manager',
  hr_specialist: 'HR Specialist',
  jurisdiction_manager: 'Jurisdiction Manager',
  jurisdiction_requestor: 'Jurisdiction Requestor',
  sales_marketing: 'Sales & Marketing',
};

const roleLabel = (role) =>
  ROLE_LABELS[role] || (role || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const UserProfileMenu = ({ user }) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    try {
      const { clearPermissionsCache } = require('../../hooks/usePermissions');
      clearPermissionsCache();
    } catch (e) { /* hook module not loaded yet */ }
    try {
      const { queueLogoutNotice } = require('../../utils/logoutNotice');
      queueLogoutNotice('manual');
    } catch (_) { /* helper not loaded */ }
    navigate('/login', { replace: true });
  };

  const initials = user?.full_name?.split(' ').map((n) => n[0]).join('').toUpperCase().substring(0, 2)
    || user?.email?.substring(0, 2).toUpperCase() || 'OU';

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <div className="user-badge" data-testid="profile-chip">
          <div className="initials">{initials}</div>
          <div className="flex flex-col items-start leading-tight">
            <span className="text-xs font-semibold">
              {user?.full_name || user?.email || 'User'}
            </span>
            <span className="text-[10px] opacity-75">{roleLabel(user?.role)}</span>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 mt-2 border-0 shadow-lg">
        {/* ADMIN-HOME-001 (Jun 2026): the User Management shortcut was
            removed — administrative functions live in the Admin Portal
            navigation, not the profile menu. */}
        <DropdownMenuItem
          onClick={handleLogout}
          data-testid="profile-logout-btn"
          className="flex items-center gap-3 py-3 px-3 cursor-pointer hover:bg-red-50"
        >
          <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
            <LogOut className="h-4 w-4 text-red-600" />
          </div>
          <p className="text-sm font-medium text-red-600">Logout</p>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserProfileMenu;
